
import React from 'react';
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface OrderProductItemProps {
  product: {
    id: string;
    name: string;
    price: number;
    quantity: number;
    image_url?: string;
    product_id?: string | null;
    total?: number;
    currency?: string;
  };
  onRemove: (id: string) => void;
  showActions?: boolean;
}

const OrderProductItem = ({ product, onRemove, showActions = true }: OrderProductItemProps) => {
  // Handle case where price might be undefined
  const price = typeof product.price === 'number' ? product.price : 0;
  
  // Calculate total if it's not provided
  const total = product.total !== undefined ? product.total : (price * product.quantity);
  
  // Use DZD as default currency if none is provided (changed from EUR to DZD)
  const currency = product.currency || "DZD";
  
  // Get currency symbol
  const getCurrencySymbol = (currencyCode: string) => {
    switch(currencyCode) {
      case 'EUR': return '€';
      case 'USD': return '$';
      case 'GBP': return '£';
      case 'DZD': return 'DA';
      case 'MAD': return 'MAD';
      case 'TND': return 'TND';
      default: return currencyCode;
    }
  };
  
  const currencySymbol = getCurrencySymbol(currency);
  
  return (
    <TableRow className={cn(
      "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors",
      !showActions && "cursor-pointer"
    )}>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {product.image_url && (
            <div className="w-8 h-8 bg-gray-100 rounded-md overflow-hidden">
              <img 
                src={product.image_url} 
                alt={product.name} 
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <span className="text-gray-800 dark:text-gray-200">{product.name}</span>
        </div>
      </TableCell>
      <TableCell className="text-right text-gray-700 dark:text-gray-300">
        {price.toFixed(2)} {currencySymbol}
      </TableCell>
      <TableCell className="text-center text-gray-700 dark:text-gray-300">{product.quantity}</TableCell>
      <TableCell className="text-right font-medium text-gray-800 dark:text-gray-200">
        {total.toFixed(2)} {currencySymbol}
      </TableCell>
      {showActions && (
        <TableCell className="text-right">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(product.id)}
            className="h-8 w-8 p-0 rounded-full text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Supprimer</span>
          </Button>
        </TableCell>
      )}
    </TableRow>
  );
};

export default OrderProductItem;
